import axios from 'axios';

const BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');

/**
 * Resolve a public charger identifier to its public-facing summary.
 *
 * The backend `GET /api/public/chargers/:identifier` accepts either:
 *   - a canonical GCEV code (`GCEV\d{7}`), or
 *   - a legacy internal `stations.station_id` printed on stickers
 *     from the pre-GCEV era (base62 shorts like `KTurSSgk`,
 *     manufacturer serials like `AU105ACG00142P`, dev IDs like
 *     `charger001`).
 *
 * Legacy identifiers are resolved to their `stations.public_id` GCEV
 * server-side. The response always carries the CANONICAL GCEV in the
 * `chargerCode` field on success — never the raw legacy identifier
 * the caller sent. On 404, the raw input is echoed back under
 * `identifier` (not `chargerCode`) so support copy can quote the
 * sticker the customer actually scanned.
 *
 * @param {string} identifier - Raw QR-embedded identifier (GCEV or legacy).
 * @returns {Promise<{
 *   ok: true,
 *   chargerCode: string,                                          // canonical GCEV
 *   status: 'activated' | 'retired' | 'not_yet_active' | 'inactive' | 'network_removed',
 *   chargeable: boolean,
 *   stationId: string | null,
 *   displayName: string | null,
 *   locationText: string | null,
 * }>}
 *
 * `network_removed` — the identifier resolved to a real charger but
 * the station is no longer on the public GreenCharge network.
 * Distinct from `retired` (identity-lifecycle terminal state),
 * maintenance (transient), and not-found (unknown identifier).
 *
 * Throws on network/HTTP error. 404 responses surface as
 * `err.response.status === 404` so the caller can render a
 * "not found" state.
 */
export async function resolvePublicCharger(identifier) {
  const url = `${BASE}/public/chargers/${encodeURIComponent(identifier)}`;
  const response = await axios.get(url);
  return response.data;
}
